import { BaseClient } from '../base-client';
import type {
  CreateProductInput,
  ImportProductsResult,
  ListProductsQuery,
  PatchProductInput,
  Product,
  SetProductStatusInput,
} from './types';

type ListResponse = { data: Product[]; meta: { total: number; limit: number; offset: number } };
type One = { data: Product };
type ImportResponse = { data: ImportProductsResult };

export class ProductsClient extends BaseClient {
  list(query: ListProductsQuery = {}): Promise<ListResponse> {
    return this.request('/api/activities', { query: normalize(query) });
  }

  /** Batch fetch products by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Product>('/api/activities/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/activities/${encodeURIComponent(id)}`);
  }

  create(input: CreateProductInput): Promise<One> {
    return this.request('/api/activities', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchProductInput): Promise<One> {
    return this.request(`/api/activities/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  setStatus(id: string, input: SetProductStatusInput): Promise<One> {
    return this.request(`/api/activities/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/activities/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  /**
   * Download the tenant's products as an XLSX blob. Filter mirrors the
   * list-page filters (status / kind / role / search); pass {} to export everything.
   */
  exportXlsx(
    filter: {
      status?: number;
      internal?: boolean;
      accessibleByRoleId?: string;
      serviceRoleId?: string;
      q?: string;
    } = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filter.status !== undefined) query.status = filter.status;
    if (filter.internal !== undefined) query.internal = filter.internal;
    if (filter.accessibleByRoleId !== undefined)
      query.accessibleByRoleId = filter.accessibleByRoleId;
    if (filter.serviceRoleId !== undefined) query.serviceRoleId = filter.serviceRoleId;
    if (filter.q !== undefined) query.q = filter.q;
    return this.requestBlob('/api/activities/export', { query });
  }

  /**
   * Download a blank XLSX template with just the import header row.
   * Lets users grab a correctly-formatted file without needing to
   * export first.
   */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/activities/import/template');
  }

  /**
   * Upload an .xlsx or .csv file. The server validates every row before
   * persisting any of them; rows that fail are returned in the result
   * with their per-row errors so the caller can render a report.
   *
   * Pass `idempotencyKey` to make the request safely retryable — the
   * server stores the response for the key and replays it on retry
   * instead of re-applying the import.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/activities/import', fd, { headers });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    } else if (Array.isArray(v)) {
      // CSV form. `serviceRoleIds=a,b,c` matches the server's csv parser.
      if (v.length > 0) out[k] = v.join(',');
    }
  }
  return out;
}
